import React, { useState, useEffect } from "react";
import { apiClient } from "../../../services/api/apiClient";
import EditIcon from "../../../assets/Edit.svg";
import DeleteIcon from "../../../assets/Delete.svg";

function ValvesTable({ onEdit, onDelete, submittalData }) {
  const [sizeDefinitions, setSizeDefinitions] = useState([]);

  useEffect(() => {
    if (submittalData && Array.isArray(submittalData)) {
      // Group the submittalData by spec_id like the original fetch was doing
      const groupedData = submittalData.reduce((acc, item) => {
        const { spec_id, spec_name, system_ids } = item;
        if (!acc[spec_id]) {
          acc[spec_id] = { name: spec_name, details: [], system_ids: [] };
        }
        acc[spec_id].details.push(item);
        acc[spec_id].system_ids = item.system_ids || []; // Ensure system_ids are stored
        return acc;
      }, {});
      setSizeDefinitions(groupedData);
    } else {
      setSizeDefinitions({});
    }
  }, [submittalData]);
  const handleDelete = async (specId) => {
    if (window.confirm("Are you sure you want to delete this specification?")) {
      try {
        // First, delete all size definitions associated with the specification
        const sizeDefResponse = await apiClient.delete(`/size_definitions/spec/${specId}`);
        
        // Check if the deletion was successful (the new API returns standardized responses)
        if (!sizeDefResponse.success) {
          throw new Error(sizeDefResponse.error || 'Failed to delete size definitions');
        }

        // Then, delete the specification itself
        const specResponse = await apiClient.delete(`/specifications/${specId}`);
        
        // Check if the deletion was successful
        if (!specResponse.success) {
          throw new Error(specResponse.error || 'Failed to delete specification');
        }

        // Update the UI to remove the deleted specification from the local state
        setSizeDefinitions((prevDefinitions) => {
          const updatedDefinitions = { ...prevDefinitions };
          delete updatedDefinitions[specId];
          return updatedDefinitions;
        });

        // Call the parent's onDelete to trigger refresh
        if (onDelete) {
          onDelete(specId);
        }

        alert("Specification deleted successfully.");
      } catch (error) {
        console.error("Error deleting specification:", error);
        alert("Failed to delete specification.");
      }
    }
  };
  // Responsive grid template shared by the header and every row so they stay
  // aligned. Flexible minmax/fr tracks let the columns shrink to fit narrower
  // screens (instead of overflowing and pushing Actions off-screen), while the
  // min widths keep each column readable. Content wraps within its cell.
  const gridTemplateColumns =
    "minmax(120px, 1.3fr) minmax(70px, 0.7fr) minmax(110px, 1.6fr) minmax(110px, 1.6fr) minmax(100px, 1.4fr) minmax(100px, 1.4fr) minmax(100px, 1.4fr) minmax(52px, 0.6fr)";
  const gridGap = "16px";

  return (
    <div
      className="submittal-details"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        padding: "20px",
        border: "1px solid #6B7280",
        borderRadius: "20px",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns,
          gap: gridGap,
          padding: "13.5px 0 15px 0",
          borderBottom: "1px solid #D1D5DB",
        }}
      >
        <p
          style={{
            color: "#111827",
            fontSize: "14px",
            fontFamily: "Inter",
            textAlign: "left",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          Specification/Systems
        </p>
        <p
          style={{
            color: "#111827",
            fontSize: "14px",
            fontFamily: "Inter",
            textAlign: "left",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          Sizes
        </p>
        <p
          style={{
            color: "#111827",
            fontSize: "14px",
            fontFamily: "Inter",
            textAlign: "left",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          Shutoff Valve 1
        </p>
        <p
          style={{
            color: "#111827",
            fontSize: "14px",
            fontFamily: "Inter",
            textAlign: "left",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          Shutoff Valve 2
        </p>
        <p
          style={{
            color: "#111827",
            fontSize: "14px",
            fontFamily: "Inter",
            textAlign: "left",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          Throttle Valve
        </p>
        <p
          style={{
            color: "#111827",
            fontSize: "14px",
            fontFamily: "Inter",
            textAlign: "left",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          Check Valve
        </p>
        <p
          style={{
            color: "#111827",
            fontSize: "14px",
            fontFamily: "Inter",
            textAlign: "left",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          Strainer
        </p>
        <p
          style={{
            color: "#111827",
            fontSize: "14px",
            fontFamily: "Inter",
            textAlign: "left",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          Actions
        </p>
      </div>
      {Object.entries(sizeDefinitions).map(
        ([specId, { name: specName, details, system_ids }], specIndex) => (
          <div
            key={specId}
            style={{
              borderTop: specIndex !== 0 ? "1px solid #D1D5DB" : "none",
            }}
          >
            {details.map((detail, detailIndex) => (
              <div
                key={detail.id}
                style={{
                  display: "grid",
                  gridTemplateColumns,
                  gap: gridGap,
                  padding: "8px 0",
                  borderTop: detailIndex !== 0 ? "1px solid #E5E7EB" : "none",
                }}
              >
                {detailIndex === 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4.5px",
                      padding: "0 5px",
                      minWidth: 0,
                    }}
                  >
                    <p
                      style={{
                        color: "#111827",
                        fontSize: "14px",
                        fontFamily: "Inter",
                        fontWeight: 400,
                        overflowWrap: "break-word",
                      }}
                    >
                      {specName}
                    </p>
                  </div>
                )}
                {detailIndex > 0 && <div></div>}
                <span
                  style={{
                    color: "#6B7280",
                    fontSize: "14px",
                    fontFamily: "Inter",
                    fontWeight: 400,
                    minWidth: 0,
                    overflowWrap: "break-word",
                  }}
                >
                  {detail.min_size_name} to {detail.max_size_name}
                </span>
                <p
                  style={{
                    color: "#6B7280",
                    fontSize: "14px",
                    fontFamily: "Inter",
                    fontWeight: 400,
                    minWidth: 0,
                    overflowWrap: "break-word",
                  }}
                >
                  {detail.shutoff1_name}
                </p>
                <p
                  style={{
                    color: "#6B7280",
                    fontSize: "14px",
                    fontFamily: "Inter",
                    fontWeight: 400,
                    minWidth: 0,
                    overflowWrap: "break-word",
                  }}
                >
                  {detail.shutoff2_name}
                </p>
                <p
                  style={{
                    color: "#6B7280",
                    fontSize: "14px",
                    fontFamily: "Inter",
                    fontWeight: 400,
                    minWidth: 0,
                    overflowWrap: "break-word",
                  }}
                >
                  {detail.throttle_name}
                </p>
                <p
                  style={{
                    color: "#6B7280",
                    fontSize: "14px",
                    fontFamily: "Inter",
                    fontWeight: 400,
                    minWidth: 0,
                    overflowWrap: "break-word",
                  }}
                >
                  {detail.check_name}
                </p>
                <p
                  style={{
                    color: "#6B7280",
                    fontSize: "14px",
                    fontFamily: "Inter",
                    fontWeight: 400,
                    minWidth: 0,
                    overflowWrap: "break-word",
                  }}
                >
                  {detail.strainer_name}
                </p>
                {detailIndex === 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <img
                      src={EditIcon}
                      alt="Edit"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        onEdit({
                          id: specId,
                          name: specName,
                          sizes: details,
                          system_ids: system_ids, // Pass system_ids here
                        })
                      }
                    />
                    <img
                      src={DeleteIcon}
                      alt="Delete"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleDelete(specId)}
                    />
                  </div>
                )}
                {detailIndex > 0 && <div></div>}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

export default ValvesTable;
